import { useState, useEffect, useMemo, useCallback } from "react";
import { StatCard } from "../../components/stat-card";
import { ReportHeader, ExportButton, RefreshButton } from "../../components/report-header";
import { ReportPanel, PanelTabs } from "../../components/report-panel";
import { DateRange } from "../../types";
import { AreaChart } from "../../../../components/custom/charts/area-chart";
import { BarChart } from "../../../../components/custom/charts/bar-chart";
import { DountChat } from "../../../../components/custom/charts/dount-chat";
import { formatCurrencyDecimal } from "../../../../utils/helpers";
import { ReportService, ReportSummary, RevenueByDate, RevenueByBranch, RevenueByService, OrdersByStatus } from "../../services/report-service";
import { useDataResponse } from "../../../../hooks/useResponseHandler";

// Date helpers
const today = new Date();
const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const formatDate = (d: Date) => d.toISOString().split("T")[0]; // YYYY-MM-DD

export const ExecutiveDashboard = () => {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth,
    to: today,
  });
  const [revenueTab, setRevenueTab] = useState("monthly");

  // ==================== API hooks (top-level) ====================
  const { fetch: fetchSummary, response: summaryResponse, loading: summaryLoading } = ReportService.summary();
  const { fetch: fetchRevenueByDate, response: revenueByDateResponse, loading: revenueByDateLoading } = ReportService.revenueByDate();
  const { fetch: fetchRevenueByBranch, response: revenueByBranchResponse, loading: revenueByBranchLoading } = ReportService.revenueByBranch();
  const { fetch: fetchRevenueByService, response: revenueByServiceResponse, loading: revenueByServiceLoading } = ReportService.revenueByService();
  const { fetch: fetchOrdersByStatus, response: ordersByStatusResponse, loading: ordersByStatusLoading } = ReportService.ordersByStatus();
  const { fetch: fetchBranchList, response: branchListResponse } = ReportService.branchList();

  // ==================== Local state for API data ====================
  const [summaryData, setSummaryData] = useState<ReportSummary | null>(null);
  const [revenueByDateData, setRevenueByDateData] = useState<RevenueByDate[]>([]);
  const [branchData, setBranchData] = useState<RevenueByBranch[]>([]);
  const [serviceData, setServiceData] = useState<RevenueByService[]>([]);
  const [orderStatusData, setOrderStatusData] = useState<OrdersByStatus | null>(null);

  // ==================== Response handlers ====================
  useDataResponse<ReportSummary>(summaryResponse, setSummaryData);
  useDataResponse<RevenueByDate[]>(revenueByDateResponse, setRevenueByDateData);
  useDataResponse<RevenueByBranch[]>(revenueByBranchResponse, setBranchData);
  useDataResponse<RevenueByService[]>(revenueByServiceResponse, setServiceData);
  useDataResponse<OrdersByStatus>(ordersByStatusResponse, setOrderStatusData);

  // ==================== Fetch all data ====================
  const fetchAllData = useCallback(() => {
    const params = {
      from_date: formatDate(dateRange.from),
      to_date: formatDate(dateRange.to),
    };
    fetchSummary(params);
    fetchRevenueByDate({ ...params, group_by: "month" as const });
    fetchRevenueByBranch(params);
    fetchRevenueByService(params);
    fetchOrdersByStatus(params);
    fetchBranchList();
  }, [dateRange]);

  // Fetch on mount and when dateRange changes
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Combined loading state
  const loading = summaryLoading || revenueByDateLoading || revenueByBranchLoading || revenueByServiceLoading || ordersByStatusLoading;

  // ==================== Transform data for charts ====================

  // Revenue trend chart data
  const revenueTrendData = useMemo(() => {
    if (!revenueByDateData || revenueByDateData.length === 0) {
      return { categories: [] as string[], series: [] };
    }
    const categories = revenueByDateData.map((item) => item.date);
    return {
      categories,
      series: [
        {
          name: "Doanh thu",
          data: revenueByDateData.map((item) => item.revenue),
        },
      ],
    };
  }, [revenueByDateData]);

  // Order status chart data
  const orderStatusChartData = useMemo(() => {
    if (!orderStatusData) {
      return { labels: [] as string[], data: [] as number[] };
    }
    const statusMap: { key: keyof OrdersByStatus; label: string }[] = [
      { key: "complete", label: "Hoàn thành" },
      { key: "waiting_accept", label: "Chờ xác nhận" },
      { key: "waiting_pick_up", label: "Chờ lấy hàng" },
      { key: "waiting_laundry", label: "Chờ giặt" },
      { key: "in_laundring", label: "Đang giặt" },
      { key: "ready_delivery", label: "Sẵn sàng giao" },
      { key: "in_delivery", label: "Đang giao" },
      { key: "cancel", label: "Đã hủy" },
    ];
    const filtered = statusMap.filter((s) => (orderStatusData[s.key] ?? 0) > 0);
    return {
      labels: filtered.map((s) => s.label),
      data: filtered.map((s) => orderStatusData[s.key] ?? 0),
    };
  }, [orderStatusData]);

  // Branch name map
  const branchNameMap = useMemo(() => {
    const list: any[] = branchListResponse?.data || [];
    const map: Record<number, string> = {};
    list.forEach((b: any) => { if (b.id && b.name) map[b.id] = b.name; });
    return map;
  }, [branchListResponse]);

  // Branch chart data
  const branchChartData = useMemo(() => {
    return {
      categories: branchData.map((d) => branchNameMap[d.branch_id] || `Chi nhánh #${d.branch_id}`),
      data: branchData.map((d) => d.revenue),
    };
  }, [branchData, branchNameMap]);

  // Service data with percentage
  const serviceChartData = useMemo(() => {
    const totalRevenue = serviceData.reduce((sum, s) => sum + s.revenue, 0);
    if (totalRevenue === 0) return [];
    return serviceData.map((s) => ({
      name: `Dịch vụ #${s.item_id}`,
      percentage: Math.round((s.revenue / totalRevenue) * 100),
      revenue: s.revenue,
    }));
  }, [serviceData]);

  // Compute cancel rate from summary
  const cancelRate = useMemo(() => {
    if (!summaryData || summaryData.total_orders === 0) return 0;
    return parseFloat(((summaryData.cancelled_orders / summaryData.total_orders) * 100).toFixed(1));
  }, [summaryData]);

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleExport = () => {
    console.log("Exporting report...");
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <ReportHeader
        title="Tổng quan Doanh nghiệp"
        subtitle="Dashboard dành cho Ban lãnh đạo"
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        actions={
          <>
            <RefreshButton onClick={handleRefresh} loading={loading} />
            <ExportButton onClick={handleExport} />
          </>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Doanh thu"
          value={summaryData?.total_revenue ?? 0}
          suffix=" ₫"
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Tổng đơn hàng"
          value={summaryData?.total_orders ?? 0}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Đơn hoàn thành"
          value={summaryData?.completed_orders ?? 0}
          color="purple"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Đơn đã hủy"
          value={summaryData?.cancelled_orders ?? 0}
          color="cyan"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Giá trị TB/đơn"
          value={summaryData?.avg_order_value ?? 0}
          suffix=" ₫"
          color="orange"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          loading={summaryLoading}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue Trend Chart */}
        <div className="lg:col-span-2">
          <ReportPanel
            title="Xu hướng Doanh thu"
            subtitle="So sánh doanh thu theo tháng"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            }
            actions={
              <PanelTabs
                tabs={[
                  { key: "monthly", label: "Tháng" },
                  { key: "quarterly", label: "Quý" },
                ]}
                activeTab={revenueTab}
                onChange={setRevenueTab}
              />
            }
          >
            <div className="h-80">
              {revenueByDateLoading ? (
                <div className="flex items-center justify-center h-full text-gray-500">Đang tải...</div>
              ) : revenueTrendData.categories.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">Không có dữ liệu</div>
              ) : (
                <AreaChart
                  chartData={revenueTrendData.series}
                  chartLabels={revenueTrendData.categories}
                  chartColors={["#10b981"]}
                  chartHeight={300}
                  chartLoading={revenueByDateLoading}
                />
              )}
            </div>
          </ReportPanel>
        </div>

        {/* Order Status Donut */}
        <ReportPanel
          title="Trạng thái Đơn hàng"
          subtitle="Phân bổ theo trạng thái"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          }
        >
          <div className="h-80 flex items-center justify-center">
            {ordersByStatusLoading ? (
              <div className="text-gray-500">Đang tải...</div>
            ) : orderStatusChartData.data.length === 0 ? (
              <div className="text-gray-400">Không có dữ liệu</div>
            ) : (
              <DountChat
                chartData={orderStatusChartData.data}
                chartLabels={orderStatusChartData.labels}
                chartColors={["#10b981", "#f59e0b", "#3b82f6", "#8b5cf6", "#06b6d4", "#14b8a6", "#6366f1", "#ef4444"]}
                chartHeight={280}
                chartLoading={ordersByStatusLoading}
              />
            )}
          </div>
        </ReportPanel>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Branch Performance */}
        <ReportPanel
          title="Hiệu suất Chi nhánh"
          subtitle="Doanh thu theo chi nhánh"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        >
          <div className="h-72">
            {revenueByBranchLoading ? (
              <div className="flex items-center justify-center h-full text-gray-500">Đang tải...</div>
            ) : branchChartData.categories.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">Không có dữ liệu</div>
            ) : (
              <BarChart
                chartData={[
                  {
                    name: "Doanh thu",
                    data: branchChartData.data,
                  },
                ]}
                chartCategories={branchChartData.categories}
                chartColors={["#10b981"]}
                chartHeight={260}
                chartLoading={revenueByBranchLoading}
              />
            )}
          </div>
        </ReportPanel>

        {/* Service Revenue Distribution */}
        <ReportPanel
          title="Doanh thu theo Dịch vụ"
          subtitle="Top 5 dịch vụ doanh thu cao nhất"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          }
        >
          <div className="space-y-4">
            {revenueByServiceLoading ? (
              <div className="flex items-center justify-center h-32 text-gray-500">Đang tải...</div>
            ) : serviceChartData.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-400">Không có dữ liệu</div>
            ) : (
              serviceChartData.map((service, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{service.name}</span>
                      <span className="text-sm font-bold text-gray-900">{service.percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                        style={{ width: `${service.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ReportPanel>
      </div>

      {/* Key Insights */}
      <ReportPanel
        title="Điểm nổi bật"
        subtitle="Những chỉ số quan trọng cần chú ý"
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        {summaryLoading ? (
          <div className="flex items-center justify-center h-24 text-gray-500">Đang tải...</div>
        ) : !summaryData ? (
          <div className="flex items-center justify-center h-24 text-gray-400">Không có dữ liệu</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">Tổng doanh thu</p>
                <p className="text-xs text-emerald-600 mt-1">
                  Doanh thu đạt {formatCurrencyDecimal(summaryData.total_revenue)} ₫ trong kỳ báo cáo.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-800">Đơn hoàn thành</p>
                <p className="text-xs text-blue-600 mt-1">
                  {summaryData.completed_orders}/{summaryData.total_orders} đơn hàng hoàn thành trong kỳ.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">Cần theo dõi</p>
                <p className="text-xs text-amber-600 mt-1">
                  Tỷ lệ hủy đơn {cancelRate}%, cần phân tích nguyên nhân chi tiết.
                </p>
              </div>
            </div>
          </div>
        )}
      </ReportPanel>
    </div>
  );
};

export default ExecutiveDashboard;
